import { isDeepEqual } from "@suse-o11y/is-deep-equal";
import { Slot, type Slottable } from "@suse-o11y/slot";
import { useMemo } from "@suse-o11y/use-memo";
import React from "react";
// biome-ignore lint/style/noRestrictedImports:  component usage
import type { DroppableProps as BeautifulDroppableProps, DroppableStateSnapshot } from "react-beautiful-dnd";

import { DroppableContextProvider } from "./context/droppable";
import { DraggableContent } from "./_draggable-content";
import { useIsEnabled } from "./_enabler";

const LazyDroppable = React.lazy(async () => {
	// biome-ignore lint/style/noRestrictedImports: component usage
	const { Droppable } = await import("react-beautiful-dnd");

	return { default: Droppable };
});

const FALLBACK_DROPPABLE_CONTEXT = {
	placeholder: null,
	value: {
		isDraggingOver: false,
		isUsingPlaceholder: false,
	},
};

export interface DroppableProps
	extends Slottable,
		React.HTMLAttributes<HTMLElement>,
		Omit<BeautifulDroppableProps, "droppableId" | "renderClone" | "children"> {
	droppableId?: string;
	children: React.ReactNode;
	draggableClone?: React.ReactElement;
}

export const Droppable = React.forwardRef<React.ComponentRef<typeof LazyDroppable>, DroppableProps>(
	(
		{
			asChild = false,
			droppableId,
			type,
			mode,
			isDropDisabled,
			isCombineEnabled,
			direction,
			ignoreContainerClipping,
			draggableClone,
			getContainerForClone,
			children,
			...props
		},
		ref,
	) => {
		const Root = asChild ? Slot : "div";
		const droppableIdFallback = React.useId();
		const droppableIdWithFallback = droppableId ?? droppableIdFallback;
		const isEnabled = useIsEnabled();

		if (!isEnabled) {
			return (
				<DroppableContextProvider value={FALLBACK_DROPPABLE_CONTEXT}>
					<Root {...props}>{children}</Root>
				</DroppableContextProvider>
			);
		}

		return (
			<React.Suspense
				fallback={
					<DroppableContextProvider value={FALLBACK_DROPPABLE_CONTEXT}>
						<Root {...props}>{children}</Root>
					</DroppableContextProvider>
				}
			>
				<LazyDroppable
					direction={direction}
					droppableId={droppableIdWithFallback}
					getContainerForClone={getContainerForClone}
					ignoreContainerClipping={ignoreContainerClipping}
					isCombineEnabled={isCombineEnabled}
					isDropDisabled={isDropDisabled}
					mode={mode}
					ref={ref}
					renderClone={
						draggableClone == null
							? undefined
							: ({ innerRef, draggableProps, dragHandleProps }, snapshot, rubric) => (
									<DraggableContent
										data-dragging
										dragHandleProps={dragHandleProps}
										ref={innerRef}
										rubric={rubric}
										snapshot={snapshot}
										{...draggableProps}
									>
										{draggableClone}
									</DraggableContent>
								)
					}
					type={type}
				>
					{({ innerRef, droppableProps, placeholder = null }, snapshot) => (
						<DroppableContent placeholder={placeholder} snapshot={snapshot}>
							<Root
								data-dragging-over={snapshot.isDraggingOver || undefined}
								ref={innerRef}
								{...props}
								{...droppableProps}
							>
								{children}
							</Root>
						</DroppableContent>
					)}
				</LazyDroppable>
			</React.Suspense>
		);
	},
);

interface DroppableContentProps {
	snapshot: DroppableStateSnapshot;
	placeholder: null | React.ReactElement<HTMLElement>;
	children: React.ReactElement;
}

const DroppableContent: React.FC<DroppableContentProps> = ({ snapshot, placeholder, children }) => {
	const context = useMemo(
		() => ({
			placeholder,
			value: snapshot,
		}),
		[snapshot, placeholder],
		[isDeepEqual],
	);

	return <DroppableContextProvider value={context}>{children}</DroppableContextProvider>;
};
